import React, { useCallback, useState, useMemo, useEffect } from "react";
import { SwitchTransition, CSSTransition } from "react-transition-group";

import { Store__System, PosthogStore } from "@talentbait/shared-components";

import { Tooltip } from "../../../../../Components/Overlays";
import MediaLibrary from "../TemplatePost__MediaLibrary/TemplateMediaLibrary";
import InlineImageCropper from "../../InlineImageCropper";

import {
  noop,
  getCloudinaryLinkWithoutCloudinaryCrop,
  getCloudinaryCropParamsFromURL
} from "../../utils";
import TemplatePostActions from "../TemplatePostActions";

import {
  UploaderWrapper,
  UploadCloudIcon,
  CreativeWrapper,
  CreativeAndUploaderWrapper,
  EditorWrapper,
  MediaLibraryOpenButton,
  DefaultImageWrapper,
  SaveButton
} from "./styles";
import { PreviewStore, TemplatePostStore, withRefluxStore } from "../../../../../Stores";
import useArchivePicture from "../../../../../hooks/useArchivePicture";
import PopupPreview from "../../../../global-media-library/components/popup-preview/PopupPreview";
import { useTranslation } from "react-i18next";

const Creative = ({ url, onLoad }) => {
  return <img src={url} alt="" onLoad={onLoad || noop} />;
};

const TemplatePostPhotoEditor = ({
  allowVideos,
  allowImages,
  postDataKey = "creatives",
  creatives,
  selectedCreativeIndexToEdit,
  updatePost,
  cancelUpdate,
  minHeight,
  maxHeight,
  minAspect,
  maxAspect,
  extraAspectRatios,
  channelMinHeight,
  channelMinWidth,
  width,
  borderRadius = "0",
  editable,
  canBeEmpty,
  saveUpdate,
  isLogo = false,
  isImageTemplate = true,
  updateSelectedAsset,
  selectedAsset,
  isLandScape,
  //store
  campaignData,
  userProfile,
  posthogInstance
}) => {
  const { t } = useTranslation();
  //// Creative state ////
  const creative = useMemo(
    () => creatives[selectedCreativeIndexToEdit] || creatives[0] || "",
    [creatives, selectedCreativeIndexToEdit]
  );

  const savedCreativesWithoutCrop = useMemo(
    () => creatives.map((creative) => getCloudinaryLinkWithoutCloudinaryCrop(creative)),
    [creatives]
  );

  const [croppedCreativePreview, setCroppedCreativePreview] = useState(creative);

  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(true);
  const [isPopupOpen, setIsPopupOpen] = useState(true);

  useEffect(() => {
    setCroppedCreativePreview(creative);
  }, [creative]);

  const updateSelectedCreative = useCallback(
    (creative) => {
      setCroppedCreativePreview(creative);

      const newCreatives = [...creatives];
      newCreatives[selectedCreativeIndexToEdit] = creative;

      updatePost({
        key: postDataKey,
        value: newCreatives
      });
    },
    [creatives, postDataKey, selectedCreativeIndexToEdit, updatePost]
  );

  // Opening, closing and saving logic
  const closeAndSaveCallback = useCallback(() => {
    if (croppedCreativePreview && !isLogo) {
      let updatedAssetKey = "backgroundImage";

      if (!isImageTemplate) {
        updatedAssetKey = "video";
      }

      saveUpdate({ [updatedAssetKey]: croppedCreativePreview });
    } else {
      saveUpdate();
    }

    TemplatePostActions.updateShowPictureEditor(false);
  }, [croppedCreativePreview, isLogo, saveUpdate]);

  const closeMediaLibrary = useCallback(() => {
    setIsMediaLibraryOpen(false);
    setCroppedCreativePreview(creative);
    cancelUpdate();
    TemplatePostActions.updateShowPictureEditor(false);
  }, [creative, setIsMediaLibraryOpen, cancelUpdate]);

  const openMediaLibrary = useCallback(() => setIsMediaLibraryOpen(true), [setIsMediaLibraryOpen]);

  const saveSelected = useCallback(() => {
    setIsMediaLibraryOpen(false);
    closeAndSaveCallback();
  }, [closeAndSaveCallback, setIsMediaLibraryOpen]);

  const isArchivable = (url) => {
    const creativePreview = getCloudinaryLinkWithoutCloudinaryCrop(croppedCreativePreview);

    return (
      selectedAsset?.url !== url &&
      creativePreview !== url &&
      !savedCreativesWithoutCrop.includes(url)
    );
  };

  const { archivePictureCallback } = useArchivePicture(campaignData, userProfile);

  // only use cropper if image is cloudinary
  const useCropper = useMemo(
    () =>
      croppedCreativePreview.startsWith("https://res.cloudinary.com/") ||
      (croppedCreativePreview.startsWith("https://image.shutterstock.com/") && !isLogo),

    [croppedCreativePreview, isLogo, isImageTemplate]
  );

  // get default crop params from creative
  const { creativeWithoutCrop, cropFromCreative } = useMemo(
    () =>
      useCropper
        ? {
            creativeWithoutCrop: getCloudinaryLinkWithoutCloudinaryCrop(croppedCreativePreview),
            cropFromCreative: getCloudinaryCropParamsFromURL(croppedCreativePreview)
          }
        : {},
    [croppedCreativePreview, useCropper]
  );

  const handleNewAssetPopup = (asset) => {
    updateSelectedCreative(asset);
    setIsPopupOpen(false);
    setIsMediaLibraryOpen(true);

    if (asset && !isLogo) {
      let updatedAssetKey = "backgroundImage";

      if (!isImageTemplate) {
        updatedAssetKey = "video";
      }

      saveUpdate({ [updatedAssetKey]: asset });
    } else {
      setIsMediaLibraryOpen(false);
      TemplatePostActions.updateShowPictureEditor(false);
    }

    document.body.style.overflow = "auto";
  };

  const handleClosePopup = () => {
    setIsMediaLibraryOpen(false);
    setIsPopupOpen(false);
    TemplatePostActions.updateShowPictureEditor(false);

    document.body.style.overflow = "auto";
  };

  return (
    <EditorWrapper>
      <div>
        {isMediaLibraryOpen ? (
          <SwitchTransition>
            <CSSTransition
              key={useCropper ? `cropper_${creativeWithoutCrop}` : croppedCreativePreview}
              addEndListener={(node, done) => node.addEventListener("transitionend", done, false)}
              classNames="pictureFader"
            >
              <>
                {useCropper ? (
                  <InlineImageCropper
                    creativeWithoutCrop={creativeWithoutCrop}
                    cropFromCreative={cropFromCreative}
                    setCroppedCreative={setCroppedCreativePreview}
                    minAspect={minAspect}
                    maxAspect={maxAspect}
                    minHeight={minHeight}
                    maxHeight={maxHeight}
                    channelMinHeight={channelMinHeight}
                    channelMinWidth={channelMinWidth}
                    width={width}
                    extraAspectRatios={extraAspectRatios}
                    isLogo={isLogo}
                  />
                ) : (
                  <DefaultImageWrapper width={width} height={isLandScape ? minHeight : maxHeight}>
                    <Creative url={croppedCreativePreview} />
                  </DefaultImageWrapper>
                )}
                <SaveButton onClick={saveSelected}>{t("Crop")}</SaveButton>
              </>
            </CSSTransition>
          </SwitchTransition>
        ) : (
          <CreativeAndUploaderWrapper style={{ borderRadius }}>
            {isImageTemplate && (
              <CreativeWrapper
                key={creative}
                width={width}
                minHeight={minHeight}
                maxHeight={maxHeight}
              >
                {creative && <Creative url={creative} />}
              </CreativeWrapper>
            )}

            {editable && (
              <UploaderWrapper>
                <Tooltip label="Neues Bild hochladen">
                  <MediaLibraryOpenButton onClick={openMediaLibrary}>
                    <UploadCloudIcon src="/images/upload_cloud.svg" small={isLogo} alt="" />
                  </MediaLibraryOpenButton>
                </Tooltip>
              </UploaderWrapper>
            )}
          </CreativeAndUploaderWrapper>
        )}

        {editable && (
          <PopupPreview
            isOpen={isPopupOpen}
            setIsOpen={handleClosePopup}
            companyId={campaignData?.companyId}
            companyName={campaignData?.companyName}
            campaignData={campaignData}
            emitNewImageSelection={handleNewAssetPopup}
            onlyShowLogos={isLogo}
            isOldFlow={true}
          />
        )}
      </div>
    </EditorWrapper>
  );
};

export default withRefluxStore(
  TemplatePostPhotoEditor,
  [TemplatePostStore, PreviewStore, PosthogStore, Store__System],
  ["campaignData", "userProfile", "posthogInstance"]
);
